<template>
  <div
    class="absolute bottom-full left-0 z-1 mb-5 flex w-90 flex-col space-y-5 rounded-sm border border-black/30 bg-white p-5 shadow-lg"
  >
    <div class="w-full cursor-pointer text-right" @click="emit('close')">
      <icon-base :icon="IconX" />
    </div>
    <div v-if="isCustom" class="flex flex-col space-y-5">
      <div class="flex space-x-4">
        <icon-base
          :icon="IconBack"
          class="cursor-pointer"
          height="20"
          width="20"
          @click.stop="reset(false)"
        />
        <lf-h4>{{ $t("SCORECARDS.CREATE_CUSTOM_RISK_TIER") }}</lf-h4>
      </div>
      <lf-input
        :placeholder="$t('SCORECARDS.RISK_TIER_NAME')"
        name="customRiskTierName"
        class="-mb-0"
        @key-released="handleCustomRiskTierName"
      />
    </div>

    <risk-tier-dropdown
      v-else
      v-model="riskTier"
      class="z-10"
      @custom-selected="handleCustomSelected"
    />

    <lf-textarea
      :placeholder="$t('COMMON.DESCRIPTION')"
      :value="description"
      rows="4"
      name="customRiskTierDescription"
      noresize
      :character-limit="TEXT_CHARACTER_LIMIT_MEDIUM"
      @key-released="handleDescription"
    />

    <div v-if="isCustom" class="space-y-4">
      <span>
        {{ $t("SCORECARDS.INDICATOR") }}
      </span>
      <div class="flex items-center justify-between">
        <div
          v-for="rt in DEFAULT_RT_ITEMS"
          :key="rt.indicator"
          class="flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm border"
          :class="{
            'border-gray-300': riskTier?.indicator === rt.indicator,
            'border-gray-100': riskTier?.indicator !== rt.indicator,
          }"
          @click.stop="riskTier = rt"
        >
          <icon-base
            height="18"
            width="4"
            :icon="'risk-tier-' + rt.indicator"
          />
        </div>
      </div>
    </div>

    <points-select v-model="pointsRange" :min="0" :max="currentPoints" />

    <div class="flex justify-end">
      <primary-button
        class="uppercase"
        :disabled="!currentPoints"
        @click="handleAdd"
      >
        {{ $t("COMMON.ADD") }}
      </primary-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import {
  DEFAULT_RT_ITEMS,
  TEXT_CHARACTER_LIMIT_MEDIUM,
} from "@/helpers/constants";
import { makeRandomString } from "@/helpers/formatting";

import PointsSelect from "./PointsSelect.vue";
import RiskTierDropdown from "./RiskTierDropdown.vue";
import IconBack from "@/components/icons/IconBack.vue";
import IconX from "@/components/icons/deals/IconX.vue";

import type {
  IRiskTierDropdownItem,
  IScorecardRiskTier,
} from "@/models/scorecards";
import type { PropType } from "vue";

const emit = defineEmits<{
  (eventName: "update", rt: IScorecardRiskTier): void;
  (eventName: "close"): void;
}>();

const props = defineProps({
  currentPoints: {
    type: Number,
    required: true,
  },
  rtToEdit: {
    type: Object as PropType<IScorecardRiskTier | null>,
    default: null,
  },
});

const getInitialRange = computed<[number, number]>(() =>
  !props.rtToEdit
    ? [0, props.currentPoints]
    : [props.rtToEdit.points_from || 0, props.rtToEdit.points_to || 0]
);

const riskTier = ref<IRiskTierDropdownItem | null>(
  props.rtToEdit
    ? {
        label: props.rtToEdit.name,
        indicator: props.rtToEdit.indicator,
      }
    : null
);
const isCustom = ref(false);
const description = ref(props.rtToEdit?.description || "");
const pointsRange = ref(getInitialRange.value);
const customName = ref("");

const handleCustomSelected = () => {
  isCustom.value = true;
};

const reset = (close = true) => {
  if (close) {
    emit("close");
  }
  isCustom.value = false;
  riskTier.value = null;
  customName.value = "";
  description.value = "";
  pointsRange.value = getInitialRange.value;
};

const handleCustomRiskTierName = (val: string) => {
  customName.value = val;
};

const handleDescription = (val: string) => {
  description.value = val;
};

const handleAdd = () => {
  const name = isCustom.value ? customName.value : riskTier.value?.label || "";
  const indicator = riskTier.value?.indicator || 0;
  const [points_from, points_to] = pointsRange.value;

  if (
    !props.currentPoints ||
    !name ||
    !indicator ||
    !points_to ||
    (points_from !== 0 && !points_from)
  ) {
    return;
  }

  const newRiskTier: IScorecardRiskTier = {
    id: props.rtToEdit?.id || makeRandomString(),
    name,
    indicator,
    description: description.value,
    points_from,
    points_to,
  };

  emit("update", newRiskTier);
  reset();
};

watch(
  () => props.currentPoints,
  () => {
    pointsRange.value = getInitialRange.value;
  }
);
</script>
